import React, { useEffect, useState } from 'react';

const PROPONENT_LABELS = {
  AUD: 'Audiovisivi',
  ECCL: 'Parrocchie',
  ET: 'Enti Territoriali',
  LIGHTNOPRO: 'No Profit Light',
  LIGHTPRO: 'Profit Light',
  NOPROFIT: 'No Profit',
  PROFIT: 'Profit',
  UNI: 'Università e Cus',
  portfolio: 'Portfolio',
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '-';

  if (typeof value === 'number') {
    return value.toFixed(2).replace('.', ',');
  }

  return value;
};

const defaultMetricColumns = [
  { key: 'avgValue', label: 'Media SROI' },
  { key: 'stdDevValue', label: 'Dev. Standard' },
];

// restituisce il contenuto della cella “label” (può essere testo o JSX)
const getRowLabel = (row) => {
  if (row.labelNode) return row.labelNode;

  if (row.proponent) {
    return PROPONENT_LABELS[row.proponent] || row.proponent;
  }
  if (row.name) return row.name;
  if (row.label) return row.label;
  return '';
};

// rende una cella valore: se è JSX lo lascia così, altrimenti formatta
const renderCell = (value) => {
  if (React.isValidElement(value)) return value;
  return formatValue(value);
};

export function MetricsTable({
  data,
  columns = defaultMetricColumns,
  rowsPerColumn = 4, // quante righe per blocco in orizzontale
  breakpoint = 1024, // sotto questa larghezza torna “tutt’una”
  showHeader = true, // se false: niente header di colonna
  valueRight = false, // se true: allinea a destra i valori
}) {
  const [isNarrow, setIsNarrow] = useState(false);

  const rows = Array.isArray(data) ? data : [data];

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsNarrow(window.innerWidth <= breakpoint);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  const effectiveRowsPerColumn = isNarrow ? rows.length : rowsPerColumn;

  const chunks = [];
  for (let i = 0; i < rows.length; i += effectiveRowsPerColumn) {
    chunks.push(rows.slice(i, i + effectiveRowsPerColumn));
  }

  const maxRows = chunks.length === 0 ? 0 : Math.max(...chunks.map((c) => c.length));

  return (
    <div className='metrics-table-wrapper'>
      <table className='metrics-table'>
        {showHeader && (
          <thead>
            <tr>
              {chunks.map((_, chunkIndex) => (
                <React.Fragment key={chunkIndex}>
                  <th className='mt-label-header' />
                  {columns.map((col, i) => (
                    <React.Fragment key={col.key}>
                      <th className='mt-divider'>{i > 0 ? '|' : ''}</th>
                      <th className='mt-header'>{col.label}</th>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
        )}

        <tbody>
          {Array.from({ length: maxRows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {chunks.map((chunk, chunkIndex) => {
                const row = chunk[rowIndex];

                if (!row) {
                  return (
                    <React.Fragment key={`${chunkIndex}-empty`}>
                      <td className='mt-label'></td>
                      {columns.map((col, i) => (
                        <React.Fragment key={col.key}>
                          <td className='mt-divider'>{i > 0 ? '|' : ''}</td>
                          <td className='mt-value'></td>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                }

                const rowKey = row.proponent || row.name || row.id || `${chunkIndex}-${rowIndex}`;

                return (
                  <React.Fragment key={rowKey}>
                    <td className='mt-label'>{getRowLabel(row)}</td>

                    {columns.map((col, i) => (
                      <React.Fragment key={col.key}>
                        <td className='mt-divider'>{i > 0 ? '|' : ''}</td>
                        <td className={`mt-value ${valueRight && 'text-right'}`}>
                          {renderCell(row[col.key])}
                        </td>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
